import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Label,
} from "@getgreenline/blaze-ui";
import { Search } from "lucide-react";
import { useState } from "react";
import type { BrandUserListItem } from "@/services/staff-brand-users.service";
import { getInitials } from "@/utils/strings";
import { BrandUserAssignmentForm } from "./brand-user-assignment-form";

type AssignableBrand = BrandUserListItem["brands"][number];

type AddUserDialogProps = {
  brands: AssignableBrand[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function AddUserDialog({
  brands,
  open,
  onOpenChange,
}: AddUserDialogProps) {
  const [query, setQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<AssignableBrand | null>(
    null,
  );
  const normalizedQuery = query.trim().toLowerCase();
  const filteredBrands = normalizedQuery
    ? brands.filter((brand) =>
        brand.name.toLowerCase().includes(normalizedQuery),
      )
    : brands;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setQuery("");
      setSelectedBrand(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="tw:max-w-2xl">
        {selectedBrand ? (
          <BrandUserAssignmentForm
            brand={selectedBrand}
            cancelLabel="Back"
            onCancel={() => setSelectedBrand(null)}
            onSuccess={() => handleOpenChange(false)}
          />
        ) : (
          <div className="tw:space-y-5">
            <DialogHeader>
              <DialogTitle>Add user</DialogTitle>
              <DialogDescription>
                Choose an onboarded brand, then assign or invite a user.
              </DialogDescription>
            </DialogHeader>

            <div className="tw:space-y-2">
              <Label htmlFor="onboarded-brand-search">Brand</Label>
              <InputGroup>
                <InputGroupAddon align="inline-start">
                  <Search className="tw:size-4" aria-hidden="true" />
                </InputGroupAddon>
                <InputGroupInput
                  id="onboarded-brand-search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search onboarded brands"
                  autoComplete="off"
                />
              </InputGroup>
            </div>

            <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:bg-background">
              {brands.length === 0 ? (
                <p className="tw:p-4 tw:text-sm tw:text-muted-foreground">
                  No onboarded brands are available. Add a brand first.
                </p>
              ) : null}

              {brands.length > 0 && filteredBrands.length === 0 ? (
                <p className="tw:p-4 tw:text-sm tw:text-muted-foreground">
                  No onboarded brands match “{query.trim()}”.
                </p>
              ) : null}

              {filteredBrands.length > 0 ? (
                <ul className="tw:max-h-80 tw:divide-y tw:overflow-y-auto">
                  {filteredBrands.map((brand) => (
                    <li key={brand.id}>
                      <button
                        type="button"
                        aria-label={brand.name}
                        className="tw:flex tw:w-full tw:items-center tw:gap-3 tw:px-4 tw:py-3 tw:text-left tw:text-foreground tw:transition-colors hover:tw:bg-muted/50 focus-visible:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-ring focus-visible:tw:ring-inset"
                        onClick={() => setSelectedBrand(brand)}
                      >
                        <Avatar className="tw:size-10 tw:shrink-0">
                          {brand.avatarImageUrl ? (
                            <AvatarImage
                              src={brand.avatarImageUrl}
                              alt={`${brand.name} icon`}
                            />
                          ) : null}
                          <AvatarFallback>
                            {getInitials(brand.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:font-medium">
                          {brand.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
